import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Heading, Text, HStack, VStack, SimpleGrid, Tabs, TabList, Tab, TabPanels, TabPanel, Badge, Table, Thead, Tbody, Tr, Th, Td, Spinner, Button, Circle, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import * as routesNames from "../../constant/routes";
import StatusBadge from "./statusbadge";
import InvoiceTable from "./invoicetable";
import { CustomerProjectList } from "./customerProjectList";
import { BillingProfileForm } from "./billingProfileForm";
import { showDate } from "../../utilities/helper";
import { cardProps, ICONS, StatCard, thProps } from "./billingui";
import ClientProfileView from "./customerprofiletabs";
import CustomerProjectBilling from "./customerProjectBilling";

export const inr = (n: number | null | undefined) => "₹" + Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const tabStyle = {
  fontSize: "sm",
  color: "gray.500",
  px: 4,
  _selected: { color: "blue.600", borderColor: "blue.600", fontWeight: "semibold" },
} as const;

export default function CustomerDetail() {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const customerId = Number(id);
  const [c, setC] = useState<any>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);

  const customer = location.state?.customer || c;

  const load = async () => {
    try {
      const response = await fetchApi<any>(`customers/${customerId}`, "GET", null, null, "");

      if (response) {
        setC(response);
      } else {
        setError("Customer not found");
      }
    } catch {
      setError("Customer not found");
    }
  };
  useEffect(() => {
    load();
  }, [customerId]);

  if (error) return <Box p={6}>{error}</Box>;
  if (!c)
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );

  return (
    <Box p={6}>
      <Breadcrumb mb={4} fontSize="sm" color="gray.500">
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to={routesNames.CUSTOMERLIST}>
            Customers
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink color="gray.700">{c.name}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="sm" color="gray.700">
          Customer Details
        </Heading>
        <Button
          leftIcon={<ArrowBackIcon />}
          size="sm"
          variant="outline"
          bg="white"
          borderColor="#d0d5dd"
          _hover={{ bg: "#f9fafb" }}
          onClick={() => {
            if (location.state?.from) {
              navigate(location.state.from);
            } else {
              navigate(routesNames.CUSTOMERLIST);
            }
          }}
        >
          Back
        </Button>
      </Flex>

      {/* Header card */}
      <HStack {...cardProps} p={5} mb={5} spacing={5} align="flex-start">
        <Circle size="56px" bg="blue.500" color="white" fontSize="2xl" fontWeight="semibold" flexShrink={0}>
          {String(c.name ?? "?")
            .charAt(0)
            .toUpperCase()}
        </Circle>
        <Box flex={1} minW={0}>
          <HStack spacing={2} mb={0.5}>
            <Heading size="md">{c.name}</Heading>
            <Badge colorScheme={c.status ? "green" : "gray"} borderRadius="full" px={2}>
              {c.status ? "Active" : "Inactive"}
            </Badge>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={4}>
            {c.email || "-"}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5} fontSize="sm">
            <Field label="Customer ID" value={c.customerCode || (c.id ? `CUST-${String(c.id).padStart(3, "0")}` : "-")} />
            <Field label="Payment Terms" value={c.paymentTermsDays != null ? `Net ${c.paymentTermsDays}` : "-"} />
            <Field label="GSTIN" value={c.gstin} />
            <Field label="Address" value={c.address} />
          </SimpleGrid>
        </Box>
      </HStack>

      <Tabs index={tab} onChange={setTab} isLazy variant="line" colorScheme="blue">
        <TabList borderColor="gray.200">
          <Tab {...tabStyle}>Overview</Tab>
          <Tab {...tabStyle}>Projects</Tab>
          <Tab {...tabStyle}>Invoices</Tab>
          {/* <Tab {...tabStyle}>Billing Settings</Tab> */}
          <Tab {...tabStyle}>Client Profile</Tab>
          <Tab {...tabStyle}>Project Billing</Tab>
        </TabList>
        <TabPanels>
          {/* Overview */}
          <TabPanel px={0} pt={5}>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={5}>
              <StatCard label="Invoices" value={String(c.stats?.invoiceCount ?? 0)} icon={ICONS.file} tone="gray" />
              <StatCard label="Paid Amount" value={inr(c.stats?.paidAmount)} icon={ICONS.check} tone="green" valueColor="green.600" />
              <StatCard label="Outstanding" value={inr(c.stats?.outstanding)} icon={ICONS.alert} tone="red" valueColor="red.600" />
              <StatCard label="Last Invoice" value={c.stats?.lastInvoice?.invoiceNumber ?? "-"} icon={ICONS.clock} tone="blue" help={showDate(c.stats?.lastInvoice?.issueDate)} />
            </SimpleGrid>

            <Box {...cardProps} p={5}>
              <HStack justify="space-between" mb={3}>
                <Heading size="sm">Recent Invoices</Heading>
                <Button variant="link" size="sm" colorScheme="blue" onClick={() => setTab(2)}>
                  View All
                </Button>
              </HStack>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th {...thProps}>Invoice No.</Th>
                    <Th {...thProps}>Project</Th>
                    <Th {...thProps} isNumeric>
                      Amount
                    </Th>
                    <Th {...thProps}>Due Date</Th>
                    <Th {...thProps}>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(c.recentInvoices ?? []).map((i: any) => (
                    <Tr key={i.id}>
                      <Td fontWeight="medium">{i.invoiceNumber}</Td>
                      <Td color="gray.600">{i.projectName}</Td>
                      <Td isNumeric>{inr(i.totalAmount)}</Td>
                      <Td color="gray.600">{showDate(i.dueDate)}</Td>
                      <Td>
                        <StatusBadge status={i.status} />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>

          {/* Projects */}
          <TabPanel px={0} pt={5}>
            <CustomerProjectList customerId={customerId} fallbackProjects={c.projects} selectedCompanyId={customer?.company_ID ?? c?.company_ID} selectedPocId={customer?.poc_ID ?? c?.poc_ID} />
          </TabPanel>

          {/* Invoices */}
          <TabPanel px={0} pt={5}>
            <InvoiceTable customerId={customerId} />
          </TabPanel>

          {/* Billing settings */}
          {/* <TabPanel px={0} pt={5}>
            <BillingProfileForm customerId={customerId} initial={c.billingProfile} onSaved={load} />
          </TabPanel> */}

          {/* Client Profile */}
          <TabPanel px={0} pt={5}>
            <ClientProfileView customer={c} onSaved={load} />
          </TabPanel>

          {/* Project Billing */}
          <TabPanel px={0} pt={5}>
            <CustomerProjectBilling customerId={customerId} fallbackProjects={c.projects} companyId={customer?.company_ID ?? c?.company_ID} pocId={customer?.poc_ID ?? c?.poc_ID} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <VStack align="start" spacing={0.5}>
    <Text color="gray.500" fontSize="xs">
      {label}
    </Text>
    <Text fontWeight="medium" color="gray.800">
      {value || "-"}
    </Text>
  </VStack>
);
